import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useTranslation } from '../i18n'
import type { FailingTest, FailingTestsReport, Project } from '../types'

function matchesFilter(test: FailingTest, filter: string): boolean {
  return `${test.className}.${test.methodName}`.toLowerCase().includes(filter.toLowerCase())
}

function formatDuration(durationMs: number): string {
  return durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(1)} s`
}

export function FailingTests() {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const [searchParams] = useSearchParams()
  const buildId = searchParams.get('buildId')
  const report = useApi<FailingTestsReport>(
    buildId ? `/tests/failing?buildId=${encodeURIComponent(buildId)}` : '/tests/failing'
  )
  const projects = useApi<Project[]>('/projects')

  const projectNameById = new Map(
    (projects.data ?? []).map((project) => [project.id, project.name])
  )
  const failingTests = report.data?.failingTests ?? []
  const failedCount = failingTests.filter((test) => test.status === 'FAILED').length
  const errorCount = failingTests.filter((test) => test.status === 'ERROR').length
  const visibleTests = failingTests.filter((test) => matchesFilter(test, filter))

  return (
    <div data-testid="failing-tests-page">
      <div className="page-header">
        <div className="page-title-group">
          <h1 className="page-title">{t('page.failingTests')}</h1>
          {report.data?.build && (
            <span
              style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}
              data-testid="failing-tests-build"
            >
              {t('label.inBuild', {
                buildId: report.data.build.id,
                date: new Date(report.data.build.timestamp).toLocaleString(),
              })}
            </span>
          )}
        </div>
      </div>

      {report.loading ? (
        <div className="card" data-testid="failing-tests-loading">
          {t('label.loading')}
        </div>
      ) : !report.data?.build ? (
        <div className="card" data-testid="failing-tests-no-build">
          {t('empty.noBuilds')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2" style={{ marginBottom: '1.5rem' }}>
            <div className="card stat-card" data-testid="failing-count-failed">
              <span className={`stat-value ${failedCount > 0 ? 'stat-error' : ''}`}>
                {failedCount}
              </span>
              <span className="stat-label">{t('status.failed')}</span>
            </div>
            <div className="card stat-card" data-testid="failing-count-error">
              <span className={`stat-value ${errorCount > 0 ? 'stat-error' : ''}`}>
                {errorCount}
              </span>
              <span className="stat-label">{t('status.error')}</span>
            </div>
          </div>

          <div className="card">
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="failing-filter">{t('label.filterByTestName')}</label>
              <input
                id="failing-filter"
                data-testid="failing-filter"
                className="input"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={t('placeholder.testNameFilter')}
              />
            </div>

            {visibleTests.length === 0 ? (
              <div
                style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
                data-testid="no-failing-tests"
              >
                {t('empty.noFailingTests')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table" data-testid="failing-tests-list">
                  <thead>
                    <tr>
                      <th>{t('label.test')}</th>
                      <th>{t('label.status')}</th>
                      <th>{t('label.duration')}</th>
                      <th>{t('label.project')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTests.map((test) => (
                      <tr key={test.id} data-testid="failing-test-row">
                        <td>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            <div>{test.className}</div>
                            <div style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              test.status === 'FAILED' ? 'status-failed' : 'status-error'
                            }`}
                          >
                            {test.status}
                          </span>
                        </td>
                        <td>{formatDuration(test.durationMs)}</td>
                        <td>
                          {test.projectIds.length === 0
                            ? t('label.unassigned')
                            : test.projectIds.map((id) => projectNameById.get(id) ?? id).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
